import { Button, Icon } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import Webcam from 'react-webcam'

import '../../../asset/css/webcam.css'
import Player, { videoTypes } from './components/Player'
import useWindowSize from './hooks/useWindowSize'

const WebcamStreamCapture = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation()

  // ainda não é permitido a escolha do tipo de vídeo, mas já estou abrindo o caminho
  const [videoType] = useState(videoTypes[0])

  // camera já está aberta?
  const [isLoading, setIsLoading] = useState(true)

  // gerenciar os dispositivos e qual é o atual
  const [deviceId, setDeviceId] = useState({})
  const [devices, setDevices] = useState([])

  useEffect(() => {
    navigator.getMedia =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia
    navigator.getMedia(
      {
        video: true
      },
      (mediaStream) => {
        setIsLoading(false)
        const track = mediaStream.getVideoTracks()[0]
        const settings = track.getSettings()
        const { deviceId: currentDeviceId } = settings
        setDeviceId(currentDeviceId)
      },
      () => undefined
    )
  }, [])

  const handleDevices = useCallback(
    (mediaDevices) =>
      setDevices(mediaDevices.filter(({ kind }) => kind === 'videoinput')),
    [setDevices]
  )

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices)
  }, [handleDevices])

  const handleChangeDevice = (deviceId) => {
    setDeviceId(deviceId)
  }

  // Pegar tamanho da tela atual e passar os valore como parametro do componente Webcam
  const size = useWindowSize()

  const isLandscape = size.height <= size.width
  const ratio = isLandscape
    ? size.width / size.height
    : size.height / size.width

  const videoConstraints = useMemo(
    () =>
      deviceId && typeof deviceId === 'string'
        ? {
            aspectRatio: ratio,
            deviceId
          }
        : {
            facingMode: 'user',
            aspectRatio: ratio
          },
    [deviceId, ratio]
  )

  const webcamRef = useRef(null)
  const mediaRecorderRef = useRef(null)

  // Estados e métodos para criar um stream
  const [capturing, setCapturing] = useState(false)
  const [recordedChunks, setRecordedChunks] = useState([])
  const [url, setUrl] = useState('')

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data))
      }
    },
    [setRecordedChunks]
  )

  const createUrl = useCallback(() => {
    if (recordedChunks.length) {
      const blob = new global.Blob(recordedChunks, {
        type: videoType
      })
      const urlBlob = URL.createObjectURL(blob)
      setUrl(urlBlob)
      setRecordedChunks([])
    }
  }, [recordedChunks, videoType])

  const handleStartCaptureClick = useCallback(() => {
    const stream = webcamRef.current?.stream
    if (!stream) return

    setCapturing(true)
    mediaRecorderRef.current = new global.MediaRecorder(stream, {
      mimeType: videoType
    })
    mediaRecorderRef.current.addEventListener(
      'dataavailable',
      handleDataAvailable
    )
    mediaRecorderRef.current.start()
  }, [
    webcamRef,
    setCapturing,
    mediaRecorderRef,
    handleDataAvailable,
    videoType
  ])

  const handleStopCaptureClick = useCallback(() => {
    if (!mediaRecorderRef.current) return

    mediaRecorderRef.current.stop()
    setCapturing(false)
    setTimeout(() => {
      createUrl()
    }, 2000)
  }, [mediaRecorderRef, setCapturing, createUrl])

  const handleDiscard = () => {
    setRecordedChunks([])
    setUrl('')
  }

  const handleCapture = () => {
    onSuccess(url)
  }

  const handleOnCancel = () => {
    onCancel()
  }

  // Após captura do vídeo
  useEffect(() => {
    if (recordedChunks.length) {
      createUrl()
    }
  }, [recordedChunks, createUrl])

  if (isLoading) {
    return (
      <div
        className='webcam'
        style={{
          width: size.width,
          height: size.height,
          background: '#ffffff',
          position: 'fixed',
          top: '0',
          right: '0',
          bottom: '0',
          left: '0',
          zIndex: 2
        }}
      >
        <div className='d-flex justify-content-center align-items-center h-100'>
          {t('hello-yes.loading')}
        </div>
      </div>
    )
  }

  if (!deviceId) {
    return null
  }

  return (
    <div
      className='webcam'
      style={{
        width: size.width,
        height: size.height,
        background: '#ffffff',
        position: 'fixed',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        zIndex: 2
      }}
    >
      <div className='position-relative'>
        <div className='webcam-header'>
          <div className='webcam-header-elements'>
            <button
              type='button'
              className='icon-button'
              onClick={handleOnCancel}
            >
              <Icon iconName='Close' icSize='small' />
            </button>
            <span>
              {url
                ? t('activity-document-photo.webcam.preview')
                : t('activity-document-photo.webcam.camera')}
            </span>
          </div>
        </div>

        {url ? (
          <Player source={url} />
        ) : (
          <Webcam
            audio
            ref={webcamRef}
            height={size.height}
            width={size.width}
            videoConstraints={videoConstraints}
          />
        )}

        <div className='webcam-footer'>
          {url ? (
            <div className='webcam-footer-controls webcam-footer-controls-after-capture'>
              <Button onClick={handleCapture}>
                {t('activity-input-take-video.btn-use-this-video')}
              </Button>
              <Button variant='secondary' onClick={handleDiscard}>
                {t('activity-input-take-video.btn-discard')}
              </Button>
            </div>
          ) : (
            <div className='webcam-footer-controls webcam-footer-controls-before-capture'>
              <button
                type='button'
                className='icon-button'
                onClick={() => {
                  if (devices.length <= 1) return
                  const currentIndex = devices.findIndex(
                    (d) => d.deviceId === deviceId
                  )
                  const nextIndex =
                    currentIndex < devices.length - 1 ? currentIndex + 1 : 0
                  const nextDevice = devices[nextIndex]
                  if (nextDevice?.deviceId) {
                    handleChangeDevice(nextDevice.deviceId)
                  }
                }}
                disabled={devices.length <= 1}
              >
                <Icon iconName='SwitchCamera' />
              </button>
              {capturing ? (
                <button
                  type='button'
                  className='button-take-photo'
                  onClick={handleStopCaptureClick}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#dc3545',
                    border: '4px solid white',
                    cursor: 'pointer'
                  }}
                />
              ) : (
                <button
                  type='button'
                  className='button-take-photo'
                  onClick={handleStartCaptureClick}
                />
              )}
              <button type='button' className='icon-button' disabled>
                <Icon iconName='CameraFlashOff' />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WebcamStreamCapture
